<template>
  <section
    class="auth-showcase"
    aria-label="YoConstruyo"
  >
    <transition-group
      name="auth-showcase-fade"
      tag="div"
      class="auth-showcase__track"
    >
      <div
        v-for="(slide, index) in visibleSlides"
        v-show="index === current"
        :key="slide.id"
        class="auth-showcase__slide"
        :style="{ backgroundImage: `url('${imageSrc(slide)}')` }"
      >
        <div
          v-if="hasText(slide)"
          class="auth-showcase__shade"
        />
        <div
          v-if="hasText(slide)"
          class="auth-showcase__content"
        >
          <div
            v-if="slide.kicker"
            class="auth-showcase__kicker"
          >
            <span
              v-for="(line, lineIndex) in kickerLines(slide.kicker)"
              :key="lineIndex"
              class="d-block"
            >{{ line }}</span>
          </div>
          <div
            v-if="slide.kicker"
            class="auth-showcase__bar"
          />
          <h2
            v-if="slide.title || slide.highlight"
            class="auth-showcase__title"
          >
            <span v-if="slide.title">{{ slide.title }}</span>
            <span
              v-if="slide.highlight"
              class="auth-showcase__highlight"
            >{{ slide.highlight }}</span>
          </h2>
          <ul
            v-if="slide.bullets && slide.bullets.length"
            class="auth-showcase__bullets"
          >
            <li
              v-for="(bullet, bulletIndex) in slide.bullets"
              :key="bulletIndex"
            >
              <span class="auth-showcase__icon">
                <feather-icon
                  :icon="bullet.icon"
                  size="18"
                />
              </span>
              <span>{{ bullet.text }}</span>
            </li>
          </ul>
        </div>
      </div>
    </transition-group>

    <div
      v-if="visibleSlides.length > 1"
      class="auth-showcase__dots"
    >
      <button
        v-for="(slide, index) in visibleSlides"
        :key="slide.id"
        type="button"
        :class="['auth-showcase__dot', { 'auth-showcase__dot--active': index === current }]"
        :aria-label="`Ver imagen ${index + 1}`"
        @click="goTo(index)"
      />
    </div>
  </section>
</template>

<script>
import assetUrl from '@/utils/assetUrl'

const INTERVAL_MS = 6000

export default {
  props: {
    previewSlides: {
      type: Array,
      default: null,
    },
  },

  data() {
    return {
      current: 0,
      timer: null,
      fetched: Boolean(this.previewSlides),
      defaultSlide: {
        id: 'default',
        image_url: `${process.env.BASE_URL}img/login/slide-default.jpg`,
      },
    }
  },

  computed: {
    loadedSlides() {
      return this.$store.getters['loginSlides/slides'] || []
    },
    visibleSlides() {
      const source = this.previewSlides || this.loadedSlides
      if (source.length) return source
      return this.fetched ? [this.defaultSlide] : []
    },
  },

  watch: {
    visibleSlides() {
      if (this.current >= this.visibleSlides.length) this.current = 0
      this.restart()
      this.preload()
    },
  },

  mounted() {
    if (!this.previewSlides) {
      this.$store.dispatch('loginSlides/fetchLoginSlides')
        .catch(() => {})
        .finally(() => { this.fetched = true })
    }
    this.restart()
    this.preload()
  },

  beforeDestroy() {
    this.stop()
  },

  methods: {
    imageSrc(slide) {
      return assetUrl(slide.image_url, this.$store.state.apiUrl)
    },
    preload() {
      if (!this.$el || this.$el.offsetParent === null || this.visibleSlides.length < 2) return
      this.visibleSlides.forEach(slide => {
        const image = new Image()
        image.src = this.imageSrc(slide)
      })
    },
    hasText(slide) {
      return Boolean(slide.kicker || slide.title || slide.highlight || (slide.bullets && slide.bullets.length))
    },
    kickerLines(kicker) {
      return String(kicker).replace(/\.\s+/g, '.\n').split('\n').filter(Boolean)
    },
    goTo(index) {
      this.current = index
      this.restart()
    },
    next() {
      if (document.hidden) return
      this.current = (this.current + 1) % this.visibleSlides.length
    },
    stop() {
      if (this.timer) clearInterval(this.timer)
      this.timer = null
    },
    restart() {
      this.stop()
      const reducedMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches
      if (this.visibleSlides.length > 1 && !reducedMotion) {
        this.timer = setInterval(this.next, INTERVAL_MS)
      }
    },
  },
}
</script>

<style lang="scss" scoped>
$auth-green: #3ce562;

.auth-showcase {
  position: relative;
  height: 100%;
  min-height: 100%;
  overflow: hidden;
  background: #0b0f14;
}

.auth-showcase__track,
.auth-showcase__slide {
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
}

.auth-showcase__slide {
  background-size: cover;
  background-position: center top;
}

.auth-showcase__shade {
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  background: linear-gradient(90deg, rgba(5, 8, 12, 0.88) 0%, rgba(5, 8, 12, 0.6) 42%, rgba(5, 8, 12, 0) 78%);
}

.auth-showcase__content {
  position: relative;
  z-index: 1;
  max-width: 440px;
  padding: 3.25rem 3rem;
  color: #fff;
}

.auth-showcase__kicker {
  font-size: 0.75rem;
  font-weight: 600;
  letter-spacing: 0.28em;
  line-height: 1.7;
  text-transform: uppercase;
  color: rgba(255, 255, 255, 0.78);
}

.auth-showcase__bar {
  width: 34px;
  height: 4px;
  margin: 1rem 0 1.25rem;
  border-radius: 2px;
  background: $auth-green;
}

.auth-showcase__title {
  margin: 0 0 1.75rem;
  font-size: clamp(2rem, 3vw, 2.75rem);
  font-weight: 700;
  line-height: 1.08;
  color: #fff;

  span {
    display: block;
  }
}

.auth-showcase__highlight {
  color: $auth-green;
}

.auth-showcase__bullets {
  margin: 0;
  padding: 0;
  list-style: none;

  li {
    display: flex;
    align-items: center;
    gap: 0.9rem;
    margin-bottom: 1rem;
    max-width: 260px;
    font-size: 0.92rem;
    line-height: 1.3;
    color: rgba(255, 255, 255, 0.9);
  }
}

.auth-showcase__icon {
  display: inline-flex;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
  width: 38px;
  height: 38px;
  border: 1px solid rgba(255, 255, 255, 0.28);
  border-radius: 50%;
}

.auth-showcase__dots {
  position: absolute;
  left: 3rem;
  bottom: 2rem;
  z-index: 2;
  display: flex;
  gap: 0.45rem;
}

.auth-showcase__dot {
  width: 8px;
  height: 8px;
  padding: 0;
  border: 0;
  border-radius: 4px;
  background: rgba(255, 255, 255, 0.45);
  transition: width 0.3s ease, background 0.3s ease;
  cursor: pointer;
}

.auth-showcase__dot--active {
  width: 24px;
  background: $auth-green;
}

.auth-showcase-fade-enter-active,
.auth-showcase-fade-leave-active {
  transition: opacity 0.8s ease;
}

.auth-showcase-fade-enter,
.auth-showcase-fade-leave-to {
  opacity: 0;
}
</style>
